import React, { useState, useEffect } from 'react'
import { Box, Container, Grid, Paper, Typography, Button, Stack, ToggleButton, ToggleButtonGroup, IconButton, useMediaQuery, useTheme, Alert } from '@mui/material'
import { Home, Business, ArrowBack, ArrowForward, Refresh, CheckCircle } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { PackageCardSkeleton } from './SkeletonLoaders'
import { useSwipe } from '../hooks/useSwipe'
import { useLanguage } from '../context/LanguageContext'

type Package = {
  id: string
  name: string
  type: 'home' | 'business'
  price: string
  speed: string
  uploadSpeed?: string
  features: string[]
  popular: boolean
  tag?: string
  tagColor?: string
}

interface PlanCardsProps {
  showToggle?: boolean
  showHeader?: boolean
}

const getAccentColor = (speed: string, popular: boolean): string => {
  if (popular) return '#00B140'
  const n = parseInt(speed.split(/\s+/)[0])
  if (n <= 100)  return '#3B82F6'
  if (n <= 200)  return '#8B5CF6'
  if (n <= 400)  return '#F59E0B'
  if (n <= 600)  return '#00B140'
  if (n <= 1200) return '#EF4444'
  return '#EC4899'
}

const getBestFor = (speed: string, type: string): string => {
  const n = parseInt(speed.split(/\s+/)[0])
  if (type === 'business') {
    if (n <= 80)   return 'Solo traders, home offices & small teams of 1–3'
    if (n <= 160)  return 'Small teams of up to 10 using cloud apps & VoIP'
    if (n <= 250)  return 'Growing businesses with a busy office environment'
    if (n <= 600)  return 'Established businesses with high bandwidth demands'
    if (n <= 1000) return 'Enterprise teams running data-heavy operations'
    return 'Mission-critical connections for demanding organisations'
  }
  if (n <= 80)   return 'Everyday browsing & streaming for 1–2 people'
  if (n <= 160)  return 'Working from home & HD streaming for small families'
  if (n <= 250)  return 'Busy households with multiple devices running at once'
  if (n <= 400)  return 'Gaming, 4K streaming & large file downloads'
  if (n <= 600)  return 'Power users, gamers & households with lots of devices'
  if (n <= 1000) return 'Future-proof speed — nothing in your home will slow you down'
  return 'Maximum speed for the most demanding homes & creators'
}

const PlanCards: React.FC<PlanCardsProps> = ({
  showToggle = true,
  showHeader = true
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const { t } = useLanguage()
  const [planType, setPlanType] = useState<'home' | 'business'>('home')
  const [homePlans, setHomePlans] = useState<Package[]>([])
  const [businessPlans, setBusinessPlans] = useState<Package[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(0)

  useEffect(() => {
    loadPackages()
  }, [])

  const loadPackages = async () => {
    try {
      setLoading(true)
      const res = await fetch('/api/packages')
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || 'Failed to load packages')

      const packages = (data.packages || []).map((p: Package) => {
        const speedValue = p.speed.split(/\s+/)[0]
        return {
          ...p,
          popular: speedValue === '500' ? true : p.popular,
        }
      })
      setHomePlans(packages.filter((p: Package) => p.type === 'home'))
      setBusinessPlans(packages.filter((p: Package) => p.type === 'business'))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const currentPlans = planType === 'home' ? homePlans : businessPlans
  const cardsPerPage = isMobile ? 1 : isTablet ? 2 : 4
  const totalPages = Math.ceil(currentPlans.length / cardsPerPage)
  const startIndex = currentPage * cardsPerPage
  const visiblePlans = currentPlans.slice(startIndex, startIndex + cardsPerPage)

  useEffect(() => { setCurrentPage(0) }, [planType])
  useEffect(() => { setCurrentPage(0) }, [isMobile, isTablet])

  const handlePlanTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPlanType: 'home' | 'business' | null,
  ) => {
    if (newPlanType !== null) {
      setPlanType(newPlanType)
      setCurrentPage(0)
    }
  }

  const handlePrevious = () => setCurrentPage((prev) => Math.max(0, prev - 1))
  const handleNext = () => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))

  const swipeHandlers = useSwipe({
    onSwipeLeft: () => { if (currentPage < totalPages - 1) handleNext() },
    onSwipeRight: () => { if (currentPage > 0) handlePrevious() },
    threshold: 50,
  })

  return (
    <Box component="section" sx={{
      py: { xs: 8, md: 10 },
      position: 'relative',
    }}>
      <Container maxWidth="lg">
        {showHeader && (
          <Box sx={{ mb: 6 }}>
            <Typography sx={{
              color: '#8A8F98',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              fontWeight: 900,
              fontSize: 18,
              mb: 1.5,
            }}>
              {t('plans.broadbandPlans')}
            </Typography>
            <Typography component="h2" sx={{
              color: 'white',
              fontWeight: 800,
              fontSize: { xs: 32, md: 42 },
              mb: 2,
              lineHeight: 1.2,
            }}>
              {t('plans.chooseTitle')}
            </Typography>
            <Typography sx={{
              color: '#8B919C',
              fontSize: { xs: 17, md: 19 },
              lineHeight: 1.6,
              maxWidth: 600,
            }}>
              {t('plans.subtitle')}
            </Typography>
          </Box>
        )}

        {/* Home / Business toggle */}
        {showToggle && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 7 }}>
            <ToggleButtonGroup
              value={planType}
              exclusive
              onChange={handlePlanTypeChange}
              aria-label="plan type"
              sx={{
                bgcolor: '#0F1114',
                borderRadius: 2,
                border: '1px solid #1E2327',
                '& .MuiToggleButtonGroup-grouped': {
                  border: 'none',
                  borderRadius: 2,
                  px: 3,
                  color: '#5A6070',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  minHeight: { xs: 44, md: 'auto' },
                  py: { xs: 1.5, md: 1.25 },
                  '&.Mui-selected': {
                    bgcolor: '#1A1D22',
                    color: 'white',
                    '&:hover': { bgcolor: '#1E2227' },
                  },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                },
              }}
            >
              <ToggleButton value="home" aria-label="home plans">
                <Home sx={{ mr: 1, fontSize: 18 }} />
                {t('plans.home')}
              </ToggleButton>
              <ToggleButton value="business" aria-label="business plans">
                <Business sx={{ mr: 1, fontSize: 18 }} />
                {t('plans.business')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <PackageCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Alert
              severity="error"
              sx={{
                mb: 3,
                maxWidth: 600,
                mx: 'auto',
                bgcolor: 'rgba(255,82,82,0.1)',
                border: '1px solid rgba(255,82,82,0.3)',
                color: '#ff5252',
                '& .MuiAlert-icon': { color: '#ff5252' },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('plans.errorTitle')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {error.includes('Failed to fetch') || error.includes('Network')
                  ? t('plans.errorNetwork')
                  : error}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={loadPackages}
                sx={{
                  bgcolor: '#1976d2',
                  color: 'white',
                  minHeight: { xs: 44, md: 'auto' },
                  '&:hover': { bgcolor: '#1565c0' },
                }}
              >
                {t('plans.tryAgain')}
              </Button>
            </Alert>
          </Box>
        ) : currentPlans.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: '#8B919C' }}>{t('plans.noPackages')}</Typography>
          </Box>
        ) : (
          <Box
            sx={{ position: 'relative', px: { xs: 5, md: 0 } }}
            {...(isMobile && currentPlans.length > cardsPerPage ? {
              onTouchStart: swipeHandlers.onTouchStart,
              onTouchMove: swipeHandlers.onTouchMove,
              onTouchEnd: swipeHandlers.onTouchEnd,
            } : {})}
          >
            {/* Navigation arrows */}
            {currentPlans.length > cardsPerPage && (
              <>
                <IconButton
                  onClick={handlePrevious}
                  disabled={currentPage === 0}
                  aria-label="Previous packages"
                  sx={{
                    position: 'absolute',
                    left: { xs: 0, md: -64 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    width: { xs: 44, md: 48 },
                    height: { xs: 44, md: 48 },
                    bgcolor: '#0F1114',
                    border: '1px solid #1E2327',
                    color: '#C9CDD2',
                    '&:hover': { bgcolor: '#1A1D22', borderColor: '#C9CDD2' },
                    '&.Mui-disabled': { opacity: 0.25, color: '#C9CDD2' },
                  }}
                >
                  <ArrowBack sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  disabled={currentPage >= totalPages - 1}
                  aria-label="Next packages"
                  sx={{
                    position: 'absolute',
                    right: { xs: 0, md: -64 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    width: { xs: 44, md: 48 },
                    height: { xs: 44, md: 48 },
                    bgcolor: '#0F1114',
                    border: '1px solid #1E2327',
                    color: '#C9CDD2',
                    '&:hover': { bgcolor: '#1A1D22', borderColor: '#C9CDD2' },
                    '&.Mui-disabled': { opacity: 0.25, color: '#C9CDD2' },
                  }}
                >
                  <ArrowForward sx={{ fontSize: 18 }} />
                </IconButton>
              </>
            )}

            <Grid container spacing={{ xs: 3, md: 2.5 }} alignItems="flex-end">
              {visiblePlans.map((plan) => {
                const accent = getAccentColor(plan.speed, plan.popular)
                const speedNum = parseInt(plan.speed.split(/\s+/)[0])
                const maxSpeed = Math.max(...currentPlans.map(p => parseInt(p.speed.split(/\s+/)[0])))
                const speedPct = Math.round((speedNum / maxSpeed) * 100)

                return (
                <Grid item xs={12} sm={6} md={3} key={plan.id} sx={{ display: 'flex', mt: plan.popular ? { md: '-28px' } : 0 }}>
                  <Paper
                    elevation={0}
                    role="group"
                    aria-label={`${plan.name} package`}
                    tabIndex={0}
                    onClick={() => navigate(`/packages/${plan.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/packages/${plan.id}`)
                      }
                    }}
                    sx={{
                      p: { xs: 3, md: plan.popular ? 4.5 : 3.5 },
                      pt: { md: plan.popular ? 5 : 3.5 },
                      background: plan.popular
                        ? `linear-gradient(160deg, ${accent}28 0%, #0A130A 50%)`
                        : `linear-gradient(160deg, ${accent}12 0%, #0C0D0F 40%)`,
                      borderRadius: 3,
                      position: 'relative',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: plan.popular ? `${accent}55` : `${accent}25`,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'transform 200ms ease, box-shadow 200ms ease',
                      boxShadow: plan.popular
                        ? `0 0 0 1px ${accent}30, 0 24px 64px rgba(0,0,0,0.6), 0 0 80px ${accent}10`
                        : `0 8px 32px rgba(0,0,0,0.35)`,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0,
                        height: plan.popular ? 5 : 3,
                        background: accent,
                        opacity: plan.popular ? 1 : 0.7,
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: plan.popular
                          ? `0 0 0 1px ${accent}50, 0 32px 80px rgba(0,0,0,0.7), 0 0 100px ${accent}15`
                          : `0 20px 48px rgba(0,0,0,0.5), 0 0 0 1px ${accent}35`,
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${accent}80`,
                        outlineOffset: 3,
                      },
                    }}
                  >
                    {/* Popular badge */}
                    {plan.popular && (
                      <Box sx={{
                        position: 'absolute',
                        top: 14, right: 14,
                        bgcolor: accent,
                        color: 'white',
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        px: 1.25, py: 0.4,
                        borderRadius: 1,
                      }}>
                        Most Popular
                      </Box>
                    )}

                    {/* Tag (non-popular) */}
                    {plan.tag && !plan.popular && (
                      <Box sx={{
                        position: 'absolute',
                        top: 14, right: 14,
                        color: accent,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        px: 1.25, py: 0.4,
                        borderRadius: 1,
                        border: `1px solid ${accent}60`,
                        bgcolor: `${accent}12`,
                      }}>
                        {plan.tag}
                      </Box>
                    )}

                    <Stack sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Plan name */}
                      <Typography sx={{
                        color: `${accent}AA`,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        mb: 2,
                      }}>
                        {plan.name}
                      </Typography>

                      {/* Speed — the hero */}
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: `${accent}90`,
                          letterSpacing: '0.2em',
                          textTransform: 'uppercase',
                          mb: 0.75,
                        }}>
                          Download speed
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" spacing={1}>
                          <Typography sx={{
                            fontSize: { xs: 72, md: plan.popular ? 96 : 72 },
                            fontWeight: 900,
                            color: 'white',
                            lineHeight: 0.85,
                            letterSpacing: '-0.05em',
                            fontFeatureSettings: '"tnum" 1',
                            textShadow: plan.popular ? `0 0 80px ${accent}50` : `0 0 40px ${accent}25`,
                          }}>
                            {plan.speed.split(/\s+/)[0]}
                          </Typography>
                          <Typography sx={{
                            fontSize: plan.popular ? 20 : 16,
                            fontWeight: 800,
                            color: `${accent}`,
                            letterSpacing: '0.02em',
                            mb: { xs: 1, md: plan.popular ? 1.5 : 1 },
                          }}>
                            Mbps
                          </Typography>
                        </Stack>

                        {/* Speed bar */}
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{
                            height: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.06)',
                            overflow: 'hidden',
                          }}>
                            <Box sx={{
                              height: '100%',
                              width: `${speedPct}%`,
                              background: `linear-gradient(90deg, ${accent}60, ${accent})`,
                              borderRadius: 2,
                            }} />
                          </Box>
                        </Box>
                      </Box>

                      {/* Price */}
                      <Box sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="baseline" spacing={0.75}>
                          <Typography sx={{
                            fontSize: plan.popular ? 36 : 32,
                            fontWeight: 900,
                            color: 'white',
                            letterSpacing: '-0.03em',
                            lineHeight: 1,
                            fontFeatureSettings: '"tnum" 1',
                          }}>
                            £{plan.price}
                          </Typography>
                          <Typography sx={{ color: `${accent}80`, fontSize: 14, fontWeight: 700 }}>
                            /mo
                          </Typography>
                        </Stack>
                        <Typography sx={{
                          color: `${accent}60`,
                          fontSize: 11,
                          fontWeight: 600,
                          mt: 0.75,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}>
                          12-month contract
                        </Typography>
                      </Box>

                      {/* Divider */}
                      <Box sx={{ height: 1, bgcolor: `${accent}18`, mb: 2.5 }} />

                      {/* Best for callout */}
                      <Box sx={{
                        bgcolor: `${accent}0D`,
                        border: `1px solid ${accent}25`,
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1.25,
                        mb: 2,
                      }}>
                        <Typography sx={{
                          fontSize: 10,
                          color: accent,
                          fontWeight: 700,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          mb: 0.5,
                        }}>
                          Best for
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#B8C0CC', lineHeight: 1.45 }}>
                          {getBestFor(plan.speed, plan.type)}
                        </Typography>
                      </Box>

                      {/* All features */}
                      <Stack spacing={1} sx={{ flex: 1 }}>
                        {plan.features.map((feature, idx) => (
                          <Stack key={idx} direction="row" spacing={1} alignItems="center">
                            <CheckCircle sx={{ color: accent, fontSize: 13, flexShrink: 0, opacity: 0.85 }} />
                            <Typography sx={{ color: '#9BA3AF', fontSize: 12.5, lineHeight: 1.3 }}>
                              {feature}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      {/* CTA */}
                      <Button
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/packages/${plan.id}`)
                        }}
                        aria-label={`Get started with ${plan.name}`}
                        sx={{
                          mt: 3,
                          minHeight: 44,
                          fontWeight: 700,
                          fontSize: 14,
                          borderRadius: 2,
                          textTransform: 'none',
                          letterSpacing: '-0.01em',
                          bgcolor: plan.popular ? accent : `${accent}18`,
                          color: plan.popular ? 'white' : accent,
                          border: `1px solid ${accent}${plan.popular ? 'FF' : '50'}`,
                          boxShadow: plan.popular ? `0 4px 20px ${accent}40` : 'none',
                          '&:hover': {
                            bgcolor: plan.popular ? `${accent}DD` : `${accent}28`,
                            boxShadow: plan.popular ? `0 6px 28px ${accent}50` : 'none',
                          },
                        }}
                      >
                        Get started
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
                )
              })}
            </Grid>

            {/* Pagination dots */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 5 }}>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentPage(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setCurrentPage(index)
                      }
                    }}
                    aria-label={`Go to page ${index + 1}`}
                    aria-current={currentPage === index ? 'page' : undefined}
                    sx={{
                      width: currentPage === index ? 20 : 8,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: currentPage === index ? '#00B140' : '#1E2327',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: currentPage === index ? '#00B140' : '#2A2E33',
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default PlanCards
